// src/hooks/useGuide.js
import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc, updateDoc, deleteField, runTransaction, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { useAuth } from '../firebase/AuthContext';
import { GUIDE_CONFIG } from '../config/guideConfig';

// ─── Standalone functions ────────────────────────────────────────────────────
// Use these when the action happens outside a component that has useGuide in
// scope — e.g. in a service, event handler, or sibling component.


/**
 * Permanently hard-dismiss a guide for a user.
 * Use this for explicit user dismissal (e.g. "Got it" button).
 * For action-based dismissal, use incrementActionCount instead —
 * it auto-dismisses any guide whose completedBy matches the action.
 *
 * @param {string} uid     - Firebase user UID
 * @param {string} guideId - Must match a key in GUIDE_CONFIG
 */
export const hardDismissGuide = async (uid, guideId) => {
  const docRef = doc(db, 'userPreferences', uid);
  await setDoc(docRef, { guides: { [guideId]: true } }, { merge: true });
};


/**
 * Increment the count for a tracked user action.
 *
 * On the FIRST occurrence of an action (count goes from 0 → 1):
 *   - Auto-hard-dismisses any guide whose completedBy matches this actionId.
 *     This ensures users who already know a feature never see the guide for it.
 *
 * Both the count increment and any guide dismissals are written atomically
 * in a single Firestore transaction.
 *
 * @param {string} uid      - Firebase user UID
 * @param {string} actionId - Must match an entry in ALL_TRACKED_ACTIONS
 * @returns {Promise<boolean>} - Resolves to true if this was the first occurrence
 */
export const incrementActionCount = async (uid, actionId) => {
  const docRef = doc(db, 'userPreferences', uid);
  let isFirstOccurrence = false;

  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(docRef);
    const data = snap.exists() ? snap.data() : {};
    const currentCount = data.actionCounts?.[actionId] ?? 0;

    isFirstOccurrence = currentCount === 0;

    const updates = {
      actionCounts: { [actionId]: currentCount + 1 },
    };

    // On first occurrence: auto-dismiss any guide completed by this action
    if (isFirstOccurrence) {
      const guidesToDismiss = Object.entries(GUIDE_CONFIG)
        .filter(([, config]) => {
          const list = Array.isArray(config.completedBy) ? config.completedBy : config.completedBy ? [config.completedBy] : [];
          return list.includes(actionId);
        })
        .map(([guideId]) => guideId);

      if (guidesToDismiss.length > 0) {
        updates.guides = {};
        guidesToDismiss.forEach((guideId) => {
          updates.guides[guideId] = true;
        });
      }
    }

    transaction.set(docRef, updates, { merge: true });
  });

  return isFirstOccurrence;
};


/**
 * Reset an action count to zero and undo any auto-dismissals it caused.
 * Dev/testing utility only — not for use in production flows.
 *
 * @param {string} uid      - Firebase user UID
 * @param {string} actionId - The action to reset
 */
export const resetActionCount = async (uid, actionId) => {
  const docRef = doc(db, 'userPreferences', uid);

  const updates = {
    [`actionCounts.${actionId}`]: deleteField(),
  };

  // Also un-dismiss any guides whose auto-dismissal was triggered by this action
  const guidesToReset = Object.entries(GUIDE_CONFIG)
    .filter(([, config]) => {
      const list = Array.isArray(config.completedBy) ? config.completedBy : config.completedBy ? [config.completedBy] : [];
      return list.includes(actionId);
    })
    .map(([guideId]) => guideId);

  guidesToReset.forEach((guideId) => {
    updates[`guides.${guideId}`] = deleteField();
  });

  await updateDoc(docRef, updates);
};


/**
 * Subscribe to specific action counts from userPreferences.
 * Pass action IDs as individual arguments — the join is stable across renders
 * when called with string literals, avoiding unnecessary re-subscriptions.
 *
 * @param {...string} actionIds - Action IDs to watch
 * @returns {{ counts: Object.<string,number>, loaded: boolean }}
 */
export const useActionCounts = (...actionIds) => {
  const { currentUser } = useAuth();
  // Stable string key so useEffect doesn't re-run when the caller re-renders
  const idsKey = actionIds.join('\0');
  const [counts, setCounts] = useState(() =>
    Object.fromEntries(actionIds.map(id => [id, 0]))
  );
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!currentUser) return;
    const docRef = doc(db, 'userPreferences', currentUser.uid);
    return onSnapshot(
      docRef,
      (snap) => {
        const actionCounts = snap.exists() ? (snap.data().actionCounts ?? {}) : {};
        setCounts(Object.fromEntries(idsKey.split('\0').map(id => [id, actionCounts[id] ?? 0])));
        setLoaded(true);
      },
      () => {
        // On error, fail open with zero counts
        setCounts(Object.fromEntries(idsKey.split('\0').map(id => [id, 0])));
        setLoaded(true);
      }
    );
  }, [currentUser, idsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return { counts, loaded };
};


// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Hook for managing a single guide's visibility.
 *
 * A guide is visible when ALL of the following are true:
 *   - Firestore read has completed (prevents flash-then-hide)
 *   - Not hard-dismissed in Firestore
 *   - Not soft-dismissed this session
 *   - All showWhen conditions are met (checked against actionCounts in Firestore)
 *   - completedBy action count is 0 (fallback: suppresses guide if user has
 *     already done the action, even without a recorded hard-dismiss)
 *   - localCondition is true (for case-level checks passed from the component)
 *
 * On read error, fails open (shows the guide). On write error, throws loudly.
 *
 * @param {string} guideId - Must match a key in GUIDE_CONFIG
 * @param {object} [options]
 * @param {boolean} [options.localCondition=true] - For guides with case-level
 *   prerequisites (e.g. "current case has 3+ docs"). Compute this value in the
 *   host component and pass it here. The guide is hidden when false.
 *
 * @returns {{ visible: boolean, softDismiss: function, hardDismiss: function }}
 *   visible     — whether to render the guide right now
 *   softDismiss — hide for this session only, no Firestore write
 *   hardDismiss — hide permanently, writes to Firestore
 */
const useGuide = (guideId, { localCondition = true } = {}) => {
  const { currentUser } = useAuth();

  // Start all gating flags in the "hidden" state to prevent flash-then-hide
  const [isHardDismissed, setIsHardDismissed] = useState(true);
  const [showWhenMet, setShowWhenMet] = useState(false);
  const [isCompletedByDone, setIsCompletedByDone] = useState(true);
  const [isSoftDismissed, setIsSoftDismissed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!currentUser) return;

    const config = GUIDE_CONFIG[guideId];
    if (!config) {
      console.error(`useGuide: no config found for guide "${guideId}". Check guideConfig.js.`);
      return;
    }

    const docRef = doc(db, 'userPreferences', currentUser.uid);

    const unsubscribe = onSnapshot(
      docRef,
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        const guides = data.guides || {};
        const actionCounts = data.actionCounts || {};

        setIsHardDismissed(guides[guideId] === true);

        setShowWhenMet(
          config.showWhen.every(
            ({ actionId, minCount }) => (actionCounts[actionId] ?? 0) >= minCount
          )
        );

        const completedByList = Array.isArray(config.completedBy) ? config.completedBy : config.completedBy ? [config.completedBy] : [];
        const completedByCount = completedByList.reduce((sum, id) => sum + (actionCounts[id] ?? 0), 0);
        setIsCompletedByDone(completedByCount > 0);

        setLoaded(true);
      },
      (error) => {
        console.error(`useGuide: error reading preferences for "${guideId}":`, error);
        setIsHardDismissed(false);
        setShowWhenMet(config.showWhen.length === 0);
        setIsCompletedByDone(false);
        setLoaded(true);
      }
    );

    return unsubscribe;
  }, [currentUser, guideId]);

  /** Hide for this session only. No Firestore write. */
  const softDismiss = useCallback(() => {
    setIsSoftDismissed(true);
  }, []);

  /** Hide permanently. Optimistic update + Firestore write. */
  const hardDismiss = useCallback(async () => {
    setIsHardDismissed(true); // Optimistic
    if (!currentUser) return;
    await hardDismissGuide(currentUser.uid, guideId);
  }, [currentUser, guideId]);

  const visible =
    loaded &&
    !isHardDismissed &&
    !isSoftDismissed &&
    !isCompletedByDone &&
    showWhenMet &&
    localCondition;

  // Dev-only: log which conditions are blocking (or confirm visible) on every change.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    if (!loaded) return;
    if (visible) {
      console.log(`%c[Guide: ${guideId}] ✓ visible`, 'color:#10B981;font-weight:bold');
      return;
    }
    const blocking = [
      isHardDismissed   && 'hard-dismissed',
      isSoftDismissed   && 'soft-dismissed (session)',
      isCompletedByDone && 'completedBy done',
      !showWhenMet      && 'showWhen not met',
      !localCondition   && 'localCondition false',
    ].filter(Boolean);
    console.log(`%c[Guide: ${guideId}] ✗ blocked by: ${blocking.join(', ')}`, 'color:#F9574E');
  }, [guideId, loaded, visible, isHardDismissed, isSoftDismissed, isCompletedByDone, showWhenMet, localCondition]);

  return { visible, softDismiss, hardDismiss };
};

export default useGuide;